import React from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import Icon from '@mui/material/Icon';
import { useTheme as useMuiTheme } from '@mui/material/styles';

/**
 * MDSelect (Global)
 * - Placeholder'da solda mini "dropdown" ikon pill'i
 * - Sağda gold ok ikonu (icons-material gerektirmez)
 * - ✅ options verilirse seçilince otomatik label gösterir (ID yerine)
 *
 * Props:
 * - options?: Array<{ value:any, label:string }>
 * - renderValueLabel?: (value, ctx) => string | ReactNode   (override)
 * - MenuProps?: MUI Select MenuProps (anchorOrigin, transformOrigin, disableAutoFocusItem, PaperProps...)
 */
export default function MDSelect({
  label,
  value,
  onChange,
  placeholder = 'Seçiniz',
  size = 'small',
  fullWidth = true,
  helperText,
  disabled,
  sx,
  children,

  gold = '#D4AF37',
  menuMaxHeight = 360,

  // ✅ dışarıdan MenuProps al (PlanPickerCard buraya yollayacak)
  MenuProps: menuPropsIn,

  // ✅ new: options ile otomatik label göster
  options = [],

  // ✅ optional override
  renderValueLabel,

  ...rest
}) {
  const theme = useMuiTheme();
  const hasValue = value !== '' && value !== null && value !== undefined;

  const getLabelFromOptions = React.useCallback(
    (val) => {
      if (!options || !Array.isArray(options) || options.length === 0) return null;

      const hit =
        options.find((x) => x?.value === val) ||
        options.find((x) => String(x?.value) === String(val));

      return hit?.label ?? null;
    },
    [options]
  );

  const renderSelected = (selected) => {
    if (!hasValue) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 10,
              display: 'grid',
              placeItems: 'center',
              border: `1px solid ${theme.palette.divider}`,
              background: `linear-gradient(180deg, ${gold}22, ${gold}10)`,
              boxShadow: `0 10px 22px rgba(0,0,0,0.08)`,
            }}
          >
            <Icon
              sx={{
                fontSize: 18,
                color: gold,
                filter: `drop-shadow(0 2px 6px ${gold}33)`,
              }}
            >
              expand_more
            </Icon>
          </span>

          <span
            style={{
              color: theme.palette.text.secondary,
              opacity: 0.9,
              fontStyle: 'italic',
              letterSpacing: 0.1,
            }}
          >
            {placeholder}
          </span>
        </span>
      );
    }

    if (renderValueLabel) return renderValueLabel(selected, { options, theme });

    const autoLabel = getLabelFromOptions(selected);
    if (autoLabel) return autoLabel;

    if (rest.renderValue) return rest.renderValue(selected);

    return selected;
  };

  // ✅ MenuProps merge: dışarıdan gelenler ezilmez
  const mergedMenuProps = React.useMemo(() => {
    const inPaper = menuPropsIn?.PaperProps || {};
    const inPaperSx = inPaper?.sx || {};

    return {
      ...(menuPropsIn || {}), // anchorOrigin/transformOrigin/disableAutoFocusItem vb. buradan gelir
      PaperProps: {
        ...inPaper,
        sx: {
          // ✅ base tasarım
          borderRadius: '16px',
          mt: 1,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[10],
          overflow: 'hidden',
          maxHeight: menuMaxHeight,
          background: `
            radial-gradient(120% 120% at 0% 0%, ${gold}18 0%, transparent 55%),
            ${theme.palette.background.paper}
          `,
          '& .MuiMenuItem-root': {
            fontSize: 14.5,
            borderRadius: '12px',
            mx: 0.75,
            my: 0.5,
            py: 1.5,
            fontWeight: 600,
            '&:hover': { backgroundColor: theme.palette.action.hover },
            '&.Mui-selected': {
              backgroundColor: `${gold}18`,
              position: 'relative',
              '&:hover': { backgroundColor: `${gold}22` },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 8,
                top: 8,
                bottom: 8,
                width: 3,
                borderRadius: 8,
                background: gold,
              },
            },
          },

          // ✅ dışarıdan gelen sx en sonda → override edebilsin
          ...inPaperSx,
        },
      },
    };
  }, [menuPropsIn, theme, gold, menuMaxHeight]);

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      sx={{
        minWidth: 0,
        '& .MuiInputLabel-root': {
          fontSize: 12.5,
          fontWeight: 700,
          letterSpacing: 0.2,
          color: theme.palette.text.secondary,
          transform: 'translate(16px, 17px) scale(1)',
        },
        '& .MuiInputLabel-shrink': {
          transform: 'translate(16px, -8px) scale(0.85)',
          color: theme.palette.text.secondary,
        },
        ...sx,
      }}
    >
      {label ? <InputLabel>{label}</InputLabel> : null}

      <Select
        value={value}
        onChange={onChange}
        label={label}
        displayEmpty
        {...rest}
        renderValue={renderSelected}
        IconComponent={(props) => (
          <Icon
            {...props}
            sx={{
              fontSize: 24,
              right: 12,
              color: gold,
              opacity: disabled ? 0.4 : 0.95,
              pointerEvents: 'none',
              filter: `drop-shadow(0 2px 8px ${gold}33)`,
            }}
          >
            expand_more
          </Icon>
        )}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '999px',
            borderColor: 'rgba(0,0,0,.14)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,0,0,.26)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,150,136,.55)',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: '999px',
            background: 'rgba(255,255,255,.78)',
            backdropFilter: 'blur(6px)',
            minHeight: { xs: 50, sm: 46 },
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            fontWeight: 800,
            color: 'text.primary',
            padding: '10px 14px',
          },
        }}
        MenuProps={mergedMenuProps} // ✅ artık dışarıdan gelen ayarlar çalışır
      >
        {children}
      </Select>

      {helperText ? (
        <FormHelperText sx={{ mt: 0.75, mx: 0.25, color: theme.palette.text.secondary }}>
          {helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
}
